import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const SEVERITY_STYLES = {
  info: "bg-sky-600 text-white",
  warning: "bg-amber-600 text-white",
  critical: "bg-red-700 text-white",
};

const SEVERITY_ICONS = {
  info: "ℹ️",
  warning: "⚠️",
  critical: "🚨",
};

/**
 * Site-wide disaster-aware ribbon banner. Admin-triggered via /admin →
 * Disaster Alerts. Renders nothing when no active alerts target the current
 * context. Used to capture storm-spike traffic in the critical hours after
 * a named event (Hurricane Helene first weekend = 11M+ Google searches for
 * "storm damage roof repair").
 *
 * Props:
 *   - stateCode: optional 2-letter US state code. If provided, filters to
 *     alerts targeting that state OR alerts with no state targeting
 *     (global "all states" alerts). If omitted, returns ALL active alerts.
 */
export default function DisasterRibbon({ stateCode }) {
  const [alerts, setAlerts] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(localStorage.getItem("hcg-dismissed-alerts") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    let cancelled = false;
    const url = stateCode ? `${API}/disaster-alerts/active?state=${encodeURIComponent(stateCode)}` : `${API}/disaster-alerts/active`;
    axios
      .get(url)
      .then((res) => {
        if (!cancelled) setAlerts(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setAlerts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [stateCode]);

  const dismiss = (id) => {
    const next = [...new Set([...dismissedIds, id])];
    setDismissedIds(next);
    try {
      localStorage.setItem("hcg-dismissed-alerts", JSON.stringify(next));
    } catch { /* noop */ }
  };

  const visible = alerts.filter((a) => !dismissedIds.includes(a.id));
  if (visible.length === 0) return null;

  return (
    <div data-testid="disaster-ribbon" className="sticky top-0 z-40">
      {visible.map((a) => {
        const cls = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.warning;
        const icon = SEVERITY_ICONS[a.severity] || SEVERITY_ICONS.warning;
        return (
          <div
            key={a.id}
            className={`${cls} px-4 py-2.5 shadow-sm`}
            data-testid={`disaster-ribbon-${a.severity}`}
          >
            <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
              <p className="flex-1 text-sm font-medium leading-snug">
                <span className="mr-2" aria-hidden="true">{icon}</span>
                {a.headline}
                {a.cta_text && a.cta_link && (
                  <a
                    href={a.cta_link}
                    className="ml-3 inline-block rounded-full bg-white/95 px-3 py-0.5 text-xs font-semibold text-slate-900 hover:bg-white"
                    data-testid={`disaster-ribbon-cta-${a.id}`}
                  >
                    {a.cta_text} →
                  </a>
                )}
              </p>
              <button
                type="button"
                onClick={() => dismiss(a.id)}
                className="rounded p-1 text-lg leading-none text-white/90 hover:text-white"
                aria-label="Dismiss"
                data-testid={`disaster-ribbon-dismiss-${a.id}`}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
